import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Chip,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { toast } from "react-toastify";
import { sendTemplateMessage } from "../../services/apiTemplates";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  previewBox: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  variableField: {
    marginBottom: theme.spacing(2),
  },
  templateName: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  previewText: {
    whiteSpace: "pre-wrap",
    fontFamily: "inherit",
  },
}));

const SendTemplateModal = ({ open, onClose, template, ticketId, onSuccess }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [parameters, setParameters] = useState({});
  const [loading, setLoading] = useState(false);
  const [previewText, setPreviewText] = useState("");

  useEffect(() => {
    if (template) {
      // Initialize parameters from template variables
      const initialParams = {};
      if (template.variables) {
        Object.keys(template.variables).forEach((key) => {
          initialParams[key] = "";
        });
      }
      setParameters(initialParams);
      setPreviewText(template.bodyText || "");
    }
  }, [template]);

  useEffect(() => {
    // Update preview when parameters change
    if (template?.bodyText) {
      let preview = template.bodyText;
      Object.entries(parameters).forEach(([key, value]) => {
        preview = preview.replace(`{{${key}}}`, value || `{{${key}}}`);
      });
      setPreviewText(preview);
    }
  }, [parameters, template]);

  const handleParameterChange = (key, value) => {
    setParameters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSend = async () => {
    // Validate all parameters are filled
    const emptyParams = Object.entries(parameters).filter(([_, v]) => !v.trim());
    if (emptyParams.length > 0) {
      toast.error(i18n.t("templates.errors.fillAllVariables"));
      return;
    }

    setLoading(true);
    try {
      await sendTemplateMessage({
        templateId: template.id,
        ticketId,
        parameters,
      });
      toast.success(i18n.t("templates.success.sent"));
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error sending template:", error);
      toast.error(
        error.response?.data?.error || i18n.t("templates.errors.sendFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  if (!template) return null;

  const variableKeys = template.variables ? Object.keys(template.variables) : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box className={classes.templateName}>
          <span>📋</span>
          <Typography variant="h6">{template.name}</Typography>
          <Chip
            size="small"
            label={template.language?.toUpperCase()}
            color="primary"
            variant="outlined"
          />
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Preview Box */}
        <Typography variant="subtitle2" gutterBottom>
          {i18n.t("templates.preview")}:
        </Typography>
        <Box className={classes.previewBox}>
          <Typography className={classes.previewText}>{previewText}</Typography>
        </Box>

        {/* Variable Inputs */}
        {variableKeys.length > 0 && (
          <>
            <Typography variant="subtitle2" gutterBottom>
              {i18n.t("templates.fillVariables")}:
            </Typography>
            {variableKeys.map((key) => (
              <TextField
                key={key}
                className={classes.variableField}
                fullWidth
                variant="outlined"
                size="small"
                label={`{{${key}}} - ${template.variables[key] || `Variable ${key}`}`}
                value={parameters[key] || ""}
                onChange={(e) => handleParameterChange(key, e.target.value)}
                placeholder={template.variables[key] || `Enter value for {{${key}}}`}
              />
            ))}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {i18n.t("templates.buttons.cancel")}
        </Button>
        <Button
          onClick={handleSend}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            i18n.t("templates.buttons.send")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendTemplateModal;








